import { Text, Progress } from '@/shared/ui/primitives';
import type { CSSProperties, FC } from 'react';
import { ChevronRight } from 'lucide-react';
import type { ClientDetailPass } from '@/types';
import { TEST_ID_BUILDERS } from '@/testIds';
import { Pill } from '@/shared/ui';
import { getPassCardModel, type PassCardMetric } from './getPassCardModel';
import styles from './PassCard.module.css';

interface PassCardProps {
  pass: ClientDetailPass;
  onClick: (pass: ClientDetailPass) => void;
}

interface MetricBlockProps {
  metric: PassCardMetric;
  accentColor: string | undefined;
  label: string;
}

const MetricBlock: FC<MetricBlockProps> = ({ metric, accentColor, label }) => {
  const style = accentColor
    ? ({
        '--pass-card-accent': accentColor,
      } as CSSProperties)
    : undefined;
  return (
    <div className={styles.metric} style={style}>
      <div className={styles.metricHead}>
        <Text variant="title" weight="medium" className={styles.metricHeadline}>
          {metric.headline}
        </Text>
        <Text variant="subheadline">{metric.headlineSuffix}</Text>
      </div>
      <Text variant="caption" weight="regular" className={styles.progressLabel}>
        {metric.progressLabel}
      </Text>
      {metric.remainingPercent === null ? (
        <div className={styles.progressPlaceholder} aria-hidden="true" />
      ) : (
        <Progress value={Math.round(metric.remainingPercent)} label={label} />
      )}
    </div>
  );
};

export const PassCard: FC<PassCardProps> = ({ pass, onClick }) => {
  const model = getPassCardModel(pass);

  return (
    <section className={styles.card}>
      <button
        type="button"
        className={styles.cardInner}
        onClick={() => onClick(pass)}
        data-testid={TEST_ID_BUILDERS.passByName(pass.name)}
      >
        <div className={styles.header}>
          <div className={styles.title}>
            <Text variant="subheadline" weight="medium" className={styles.titleText}>
              {pass.name}
            </Text>
          </div>
          <ChevronRight size={18} className={styles.disclosure} aria-hidden="true" />
        </div>

        <div className={styles.metrics}>
          <MetricBlock
            metric={model.time}
            accentColor={model.timeAccentColor}
            label="Days remaining"
          />
          <MetricBlock
            metric={model.visits}
            accentColor={model.visitsAccentColor}
            label="Visits remaining"
          />
        </div>

        <div className={styles.footer}>
          <Text variant="caption" weight="regular">
            {model.dateLabel}
          </Text>
          <Pill tone={model.statusTone}>{model.statusLabel}</Pill>
        </div>
      </button>
    </section>
  );
};
